import React, { useMemo, useRef, useState } from "react";
import { FaMinus, FaPlus, FaStar, FaArrowLeft, FaCloudDownloadAlt, FaCheckDouble } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { useParams } from "react-router-dom";
const ProductDetailsScreen = () => {
	const [cartCounts, setCartCounts] = useState({});

	// Fetch cart from backend on mount
	React.useEffect(() => {
		const fetchCart = async () => {
			try {
				const token = localStorage.getItem("userToken");
				const headers = { "Content-Type": "application/json" };
				if (token) headers["Authorization"] = `Bearer ${token}`;
				const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cart`, {
					credentials: "include",
					headers,
				});
				if (response.status === 200) {
					const data = await response.json();
					const counts = {};
					if (data && data.data && Array.isArray(data.data.items)) {
						data.data.items.forEach(item => {
							counts[item.productId] = item.quantity;
						});
					}
					setCartCounts(counts);
				} else {
					setCartCounts({});
				}
			} catch (err) {
				setCartCounts({});
			}
		};
		fetchCart();
	}, []);

	const handleAdd = async (id) => {
	try {
		const token = localStorage.getItem("userToken");
		const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cart/add`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
			body: JSON.stringify({ productId: id, quantity: 1 }),
			credentials: "include"
		});
		const data = await response.json();

		const messageText = (data && data.message ? data.message : "").toLowerCase();
		const shouldRedirectToLogin =
			response.status === 401 ||
			messageText.includes("login") ||
			messageText.includes("access cart") ||
			messageText.includes("unauthorized");

		if (data.success) {
			setCartCounts((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
		} else {
			if (shouldRedirectToLogin) {
				setTimeout(() => {
					navigate("/login", {
						state: { loginMessage: data.message || "Login to access cart" }
					});
				}, 300);
			}
		}
	} catch (err) {
		// optional: show error
	}
};

	const handleRemove = (id) => {
		setCartCounts((prev) => {
			const newCounts = { ...prev };
			if (newCounts[id] > 1) {
				newCounts[id] = newCounts[id] - 1;
			} else {
				delete newCounts[id];
			}
			return newCounts;
		});
	};
	const navigate = useNavigate();
	const carouselRef = useRef(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const [showFullDesc, setShowFullDesc] = useState(false);
	const { productId } = useParams();
	const [product, setProduct] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	React.useEffect(() => {
		setLoading(true);
		setError(null);
		fetch(`${process.env.REACT_APP_API_URL}/api/products/${productId}`)
			.then((res) => {
				if (!res.ok) throw new Error("Failed to fetch product");
				return res.json();
			})
			.then((data) => {
				// Map backend fields to expected frontend fields
				const p = data.data || {};
				setProduct({
					images: p.productImages || [],
					name: p.productName || "",
					price: p.productSellingPrice || "",
					stock: p.productQuantity ?? "N/A",
					category: p.productCategory || "N/A",
					description: p.productDescription || "",
					rating: typeof p.productRating === "number" ? p.productRating : 0,
					ratingCount: typeof p.productRatingCount === "number" ? p.productRatingCount : 0,
				});
				setLoading(false);
			})
			.catch((err) => {
				setError(err.message);
				setLoading(false);
			});
	}, [productId]);

	const reviews = useMemo(
		() => [
			{
				id: 1,
				title: "Great comfort",
				comment: "Super light and comfy. I use it for daily walks.",
				user: "Ada K.",
				date: "Feb 10, 2026",
				rating: 5,
				verified: true,
			},
			{
				id: 2,
				title: "Nice fit",
				comment: "Fits true to size and looks good with jeans.",
				user: "Samuel O.",
				date: "Feb 08, 2026",
				rating: 4,
				verified: true,
			},
			{
				id: 3,
				title: "Good value",
				comment: "Solid build for the price. Cushioning is decent.",
				user: "Ifeoma T.",
				date: "Feb 05, 2026",
				rating: 4,
				verified: true,
			},
			{
				id: 4,
				title: "Stylish",
				comment: "Love the design and color. Got lots of compliments.",
				user: "Chisom N.",
				date: "Feb 03, 2026",
				rating: 5,
				verified: true,
			},
		],
		[]
	);

	const [suggestedProducts, setSuggestedProducts] = useState([]);
	const [suggestedLoading, setSuggestedLoading] = useState(true);
	const [suggestedError, setSuggestedError] = useState(null);

	React.useEffect(() => {
		setSuggestedLoading(true);
		setSuggestedError(null);
		fetch(`${process.env.REACT_APP_API_URL}/api/products/public`, {
			credentials: "include",
			headers: { "Content-Type": "application/json" }
		})
			.then(res => {
				if (!res.ok) throw new Error("Failed to fetch products");
				return res.json();
			})
			.then(data => {
				setSuggestedProducts(data);
				setSuggestedLoading(false);
			})
			.catch(() => {
				setSuggestedError("Error fetching products.");
				setSuggestedProducts([]);
				setSuggestedLoading(false);
			});
	}, []);

	const handleScroll = (event) => {
		const container = event.currentTarget;
		const width = container.offsetWidth || 1;
		const index = Math.round(container.scrollLeft / width);
		setActiveIndex(index);
	};

	const isLongDescription = product && product.description && product.description.length > 120;
	const descriptionText = product && product.description
		? showFullDesc
			? product.description
			: `${product.description.slice(0, 120)}...`
		: "";

	if (loading) {
		return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
	}
	if (error) {
		return <div className="min-h-screen flex items-center justify-center text-red-500">{error}</div>;
	}
	if (!product) {
		return <div className="min-h-screen flex items-center justify-center">Product not found.</div>;
	}

	return (
		<div className="min-h-screen text-gray-900" style={{ background: '#F1F3F2' }}>
			<div>
				<div className="relative">
					<div
						ref={carouselRef}
						onScroll={handleScroll}
						className="flex snap-x snap-mandatory overflow-x-auto scroll-smooth no-scrollbar"
					>
						{/* Left Icon */}
						<div
							className="flex items-center justify-center cursor-pointer"
							style={{
								background: "rgba(42,42,42,0.3)",
								borderRadius: "50%",
								width: "40px",
								height: "40px",
								position: "absolute",
								left: "8px",
								top: "12px",
								zIndex: 2,
							}}
							onClick={() => navigate(-1)}
						>
							<FaArrowLeft style={{ color: "#fff", width: "27px", height: "27px" }} />
						</div>
						{/* Carousel Images */}
						{(product.images && product.images.length > 0 ? product.images : ["/logo192.png"]).map((src, index) => (
							<div
								key={src}
								className="min-w-full snap-center"
							>
								<div className="w-full overflow-hidden bg-gray-50" style={{ aspectRatio: '1/0.8' }}>
									<img
										loading="lazy"
										src={src}
										alt={`${product.name || "Product"} ${index + 1}`}
										className="h-full w-full object-cover"
									/>
								</div>
							</div>
						))}
						{/* Add bottom padding so fixed bar does not overlap grid */}
						<div />
						{/* Right Icon */}
						<div
							className="flex items-center justify-center"
							style={{
								background: "rgba(42,42,42,0.3)",
								borderRadius: "50%",
								width: "40px",
								height: "40px",
								position: "absolute",
								right: "8px",
								top: "12px",
								zIndex: 2,
							}}
						>
							<FaCloudDownloadAlt style={{ color: "#fff", width: "27px", height: "27px" }} />
						</div>
					</div>
				</div>

				<div className="mt-3 flex justify-center gap-2">
					{(product.images && product.images.length > 0 ? product.images : ["placeholder"]).map((_, index) => (
						<span
							key={`dot-${index}`}
							className={`h-2 w-2 rounded-full ${
								activeIndex === index ? "bg-[#19C463]" : "bg-gray-300"
							}`}
						/>
					))}
				</div>

				<div className="mt-7 bg-white p-2 text-left" style={{ marginTop: '12px' }}>
					<h1 className="text-xl font-semibold">{product.name || "Unnamed Product"}</h1>
					<p className="mt-2 text-sm text-gray-600" style={{ fontSize: '16px', color: '#000000' }}>
						{descriptionText}
						{isLongDescription && (
							<button
								type="button"
								onClick={() => setShowFullDesc((prev) => !prev)}
								className="ml-2 text-[#19C463] font-medium"
							>
								{showFullDesc ? "See Less" : "See More"}
							</button>
						)}
					</p>

					<div className="mt-3 text-lg font-semibold text-gray-900">
						₦ {Number(product.price || 0).toLocaleString("en-NG")}
					</div>

					<div className="mt-2 text-sm text-gray-600">
						In stock: <span className="font-medium">{product.stock}</span>
					</div>

					<div className="mt-3 flex items-center gap-2 text-sm text-gray-700">
						<div className="flex items-center gap-1 text-[#19C463]">
							<FaStar className="h-4 w-4" />
						</div>
						<span>{product.rating} ({product.ratingCount})</span>
					</div>

					<div className="mt-2 text-sm text-gray-600">
						Category: <span className="font-medium">{product.category}</span>
					</div>
				</div>

				   <div className="mt-8" style={{ marginTop: '12px' }}>
					   <div className="flex items-center justify-between">
						   <h2 
							   className="text-lg font-semibold text-left"
							   style={{ padding: '8px' }}
						   >
							   Product ratings and reviews (<span>{reviews.length}</span>)
						   </h2>
						   <p 
							   className="font-semibold"
							   style={{ color: '#19C463', cursor: 'pointer', padding: '8px' }}
						   >
							   See all
						   </p>
					   </div>
					   <div className="mt-3 space-y-4" style={{ marginTop: '12px', padding: '0 8px' }}>
						{reviews.map((review) => (
							<div
								key={review.id}
								className="border border-gray-100"
								style={{ borderRadius: '4px', padding: '8px', gap: '2px', display: 'flex', flexDirection: 'column', background: '#ffffff' }}
							>
								<div className="flex items-center justify-between">
									<div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'flex-start' }}>
										<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
											<div className="flex items-center gap-1" style={{ color: '#2A2A2A', justifyContent: 'flex-start' }}>
												{[...Array(5)].map((_, i) => (
													<FaStar className="h-3 w-3" key={i} style={{ minWidth: '16px', minHeight: '16px' }} />
												))}
											</div>
											<div style={{ minFontSize: '14px', padding: '4px' }}>{review.title}</div>
										</div>
										<span style={{ minWidth: 'fit-content', minFontSize: '12px', color: '#000000', alignSelf: 'flex-start' }}>{review.date}</span>
									</div>
								</div>
								<p 
									className="text-sm"
									style={{ textAlign: 'left', color: '#000000', minFontSize: '12px', padding: '4px', marginTop: 0 }}
								>
									{review.comment}
								</p>
								<div className="flex flex-wrap items-center gap-2 text-xs text-gray-500" style={{ padding: '4px' }}>
									<span style={{ padding: '0 4px', minFontSize: '12px', color: '#2A2A2A' }}>{review.user}</span>
									<span>â€¢</span>
									<span style={{ flex: 1 }}></span>
									{review.verified && (
										<span style={{ display: 'flex', alignItems: 'center', color: '#19C463', gap: '2px', justifyContent: 'flex-end' }}>
											<FaCheckDouble style={{ color: '#19C463' }} />
											Verified Purchase
										</span>
									)}
								</div>
							</div>
						))}
					</div>
				</div>

				   <div className="mt-9 bg-white" style={{ marginTop: '12px' }}>
					   <div className="flex items-center justify-between w-full p-2">
						   <h2 className="text-lg font-semibold text-left">You might also like</h2>
						   <span style={{ color: '#19C463', cursor: 'pointer' }}>See all</span>
					   </div>
							   <div className="grid grid-cols-2 gap-x-[2px] gap-y-[6px]" style={{ marginTop: '12px', marginBottom: '80px' }}>
						{suggestedLoading ? (
							<div className="col-span-2 text-center py-8 text-gray-500">Loading products...</div>
						) : suggestedError ? (
							<div className="col-span-2 text-center py-8 text-red-500">{suggestedError}</div>
						) : !Array.isArray(suggestedProducts) || suggestedProducts.length === 0 ? (
							<div className="col-span-2 text-center py-8 text-gray-500">No products found.</div>
						) : (
							(suggestedProducts || []).map((product) => {
								const count = cartCounts[product._id] || 0;
								return (
									<div
										key={product._id}
										className="rounded-lg p-1 flex flex-col gap-1"
									>
										<div className="rounded-t-[4px] aspect-[10/9] w-full overflow-hidden cursor-pointer"
											onClick={() => navigate(`/product/${product._id}`)}>
											<img
												loading="lazy"
												src={product.productImages && product.productImages[0] ? product.productImages[0] : '/placeholder.png'}
												alt={product.productName}
												className="h-full w-full object-cover"
											/>
										</div>
										<div className="mt-3 text-left cursor-pointer"
											onClick={() => navigate(`/product/${product._id}`)}>
											<p className="text-sm font-medium text-gray-900 truncate">
												{product.productName} - {product.productDescription}
											</p>
											{product.productQuantity <= 5 && (
												<span className="mt-1 inline-block text-xs font-bold text-[#19C463] tracking-wider" style={{ textTransform: 'uppercase' }}>
													ONLY 1 LEFT
												</span>
											)}
											<div className="mt-2 flex items-center gap-2">
												<span className="text-sm font-semibold text-gray-900">
													₦ {Number(product.productSellingPrice || 0).toLocaleString("en-NG")}
												</span>
												<span className="text-xs text-gray-500">{product.productSold}</span>
											</div>
										</div>
										<div className="mt-3">
											{count > 0 ? (
												<div className="flex items-center justify-between px-2 py-1 w-full" style={{ width: "100%" }}>
													<button
														type="button"
														onClick={() => handleRemove(product._id)}
														className="h-8 w-8 bg-[#19C463] text-white flex items-center justify-center"
														aria-label="Decrease quantity"
														style={{ borderRadius: "4px" }}
													>
														<FaMinus className="h-3 w-3" />
													</button>
													<span className="text-sm font-bold">{count}</span>
													<button
														type="button"
														onClick={() => handleAdd(product._id)}
														className="h-8 w-8 bg-[#19C463] text-white flex items-center justify-center"
														aria-label="Increase quantity"
														style={{ borderRadius: "4px" }}
													>
														<FaPlus className="h-3 w-3" />
													</button>
												</div>
											) : (
												<button
													type="button"
													onClick={() => handleAdd(product._id)}
													className="w-full flex items-center justify-center gap-2 rounded bg-[#19C463] px-1 py-2.5 text-sm font-medium text-white transition"
													style={{ borderRadius: "4px" }}
												>
													Add to Cart
												</button>
											)}
										</div>
									</div>
								);
							})
						)}
					</div>
				</div>
			</div>

			   <div className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200">
				   <div className="mx-auto flex max-w-md items-center justify-between gap-3" style={{ padding: '10px 16px' }}>
					{cartCounts[productId] > 0 ? (
						<div className="flex items-center justify-between px-2 py-1 w-full" style={{ width: '100%' }}>
							<button
								type="button"
								onClick={() => handleRemove(productId)}
								className="h-8 w-8 bg-[#19C463] text-white flex items-center justify-center"
								aria-label="Decrease quantity"
							>
								<FaMinus className="h-4 w-4" />
							</button>
							<span className="min-w-[24px] text-center font-semibold" style={{ fontSize: '18px' }}>
								{cartCounts[productId]}
							</span>
							<button
								type="button"
								onClick={() => handleAdd(productId)}
								className="h-8 w-8 bg-[#19C463] text-white flex items-center justify-center"
								aria-label="Increase quantity"
							>
								<FaPlus className="h-4 w-4" />
							</button>
						</div>
					) : (
						<button
							type="button"
							className="w-full rounded-full bg-[#19C463] py-3 text-sm font-semibold text-white shadow-sm" style={{ borderRadius: '4px', padding: '14px' }}
							onClick={async () => {
								if (product && productId) {
									await handleAdd(productId);
								}
							}}
						>
							Add to Cart
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

export default ProductDetailsScreen;


